import { useQueryClient } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useLocale } from '@/hooks/use-locale';
import { useUserActivityStore } from '@/store/use-user-activity-store';

import { useEmbeddedLogin, useLazyGetSessionToken, useUser } from '../hooks';

declare global {
  interface Window {
    DokobitIdentity: any;
  }
}

interface DokobitOptions {
  sessionToken: string;
  callback?: (returnToken: string) => void;
  host?: string;
  locale?: string;
  debug?: boolean;
  container?: string;
  useValidator?: boolean;
  logo?: string;
  primaryColor?: string;
}

const PRIMARY_COLOR = '#9C8B7E';
const DOKOBIT_HOST = import.meta.env.VITE_DOKOBIT_HOST || 'https://id-sandbox.dokobit.com';

export const DokobitLogin = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { mutateAsync: getSessionToken } = useLazyGetSessionToken();
  const { mutateAsync: login, isPending } = useEmbeddedLogin();

  const queryClient = useQueryClient();

  const { setLastActivityTime } = useUserActivityStore();

  const { t } = useTranslation();

  const { currentLocale } = useLocale();

  const [isLoadingScript, setIsLoadingScript] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `${DOKOBIT_HOST}/js/dokobit-integration.min.js`;
    script.async = true;

    script.onload = async () => {
      try {
        const sessionResponse = await getSessionToken();

        const options: DokobitOptions = {
          sessionToken: sessionResponse.sessionToken,
          container: '#dokobit-container',
          locale: currentLocale || 'et',
          callback: async (returnToken: string) => {
            try {
              await login(returnToken);

              await queryClient.invalidateQueries({
                predicate: (query) => query.queryKey.includes('user'),
              });
              setLastActivityTime(Date.now());
            } catch (error) {
              console.error('error', error);
              setError('Failed to login with Dokobit');
            }
          },
          primaryColor: PRIMARY_COLOR,
        };

        new window.DokobitIdentity(options).init();
        setIsLoadingScript(false);
      } catch (error) {
        console.error('error', error);
        setError('Failed to load Dokobit script');
      }
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [currentLocale, getSessionToken, login]);

  return (
    <div className="mx-auto w-[300px] sm:w-[500px] md:w-full min-h-[400px]">
      {error && <div className="text-red-500">{error}</div>}

      {isLoadingScript ? (
        <div className="flex flex-col items-center gap-y-2">
          <Skeleton.Node className="w-[300px] h-[20px]" active />
          <Skeleton.Node className="w-[450px] h-[300px]" active />
        </div>
      ) : (
        !isPending && <div id="dokobit-container" ref={containerRef} />
      )}

      {isPending && (
        <LoadingSpinner
          className="w-full h-full"
          text={t('loggingYouIn', {
            ns: 'signInPage',
          })}
        />
      )}
    </div>
  );
};
